<template>
  <div class="checkin-page">
    <headerSection v-if="!standalonePreview" />
    <main class="checkin-page__main">
      <div class="checkin-page__top">
        <router-link :to="backRoute">←</router-link>
        <div><small>กิจวัตรประจำวัน</small><h1>เช็กอินให้อาหาร</h1></div>
      </div>
      <div v-if="loading" class="checkin-page__state">กำลังตรวจยอดฝากวันนี้...</div>
      <div v-else-if="error" class="checkin-page__state"><strong>{{ error }}</strong><router-link to="/">กลับหน้าหลัก</router-link></div>
      <template v-else-if="status">
        <div v-if="notice" class="checkin-notice">{{ notice }}</div>
        <div class="checkin-grid">
          <section class="feed-card">
            <span class="feed-card__eyebrow">ให้อาหารน้องวันนี้</span>
            <h2>{{ status.claimedToday ? 'อิ่มแล้ว ขอบคุณนะ!' : status.canClaim ? 'พร้อมให้อาหารแล้ว' : `ฝากให้ครบ ${status.minDeposit} บาทก่อนนะ` }}</h2>
            <p>การเช็กอินคือการให้อาหารคู่หู รับ {{ status.xpPerCheckin }} XP พร้อมเหรียญตามวันที่ต่อเนื่อง</p>
            <div class="deposit-meter">
              <div><span>ยอดฝากสำเร็จวันนี้</span><strong>{{ money(status.todayDeposit) }} / {{ money(status.minDeposit) }} บาท</strong></div>
              <div class="deposit-meter__track"><i :style="{ width: `${depositPercent}%` }"></i></div>
            </div>
            <button type="button" :disabled="!status.canClaim || claiming" @click="claim">
              {{ status.claimedToday ? 'ให้อาหารแล้ววันนี้ ✓' : claiming ? 'กำลังให้อาหาร...' : status.canClaim ? `ให้อาหาร รับ ${status.nextReward} เหรียญ + ${status.xpPerCheckin} XP` : `ขาดอีก ${money(Math.max(0, status.minDeposit - status.todayDeposit))} บาท` }}
            </button>
            <QilinStatusStrip icon="🔥" :title="`เช็กอินต่อเนื่อง ${status.streak} วัน`" :detail="`สถิติสูงสุด ${status.bestStreak} วัน · ให้อาหารแล้ว ${status.totalCheckins} ครั้ง`" />
          </section>
          <aside>
            <section class="ladder-card">
              <span>รางวัล 7 วัน</span>
              <div class="ladder">
                <div v-for="(coin, index) in status.ladder" :key="index" :class="{ active: ladderIndex === index }">
                  <small>วันที่ {{ index + 1 }}</small><b>{{ coin }}</b><em>เหรียญ</em>
                </div>
              </div>
            </section>
            <section class="calendar-card">
              <div class="calendar-card__head"><span>{{ monthLabel }}</span><small>วันที่ให้อาหาร</small></div>
              <div class="calendar-week"><span v-for="day in weekLabels" :key="day">{{ day }}</span></div>
              <div class="calendar-grid"><i v-for="(cell, index) in calendarCells" :key="index" :class="{ fed: cell.fed, today: cell.today, empty: !cell.day }">{{ cell.day }}</i></div>
            </section>
          </aside>
        </div>
      </template>
    </main>
    <footerSection v-if="!standalonePreview" />
  </div>
</template>

<script>
import moment from 'moment-timezone'
import qilinApi, { qilinErrorMessage, qilinPreviewEnabled } from '../services/qilinRetention'
import QilinStatusStrip from '../components/qilin/QilinStatusStrip.vue'

export default {
  name: 'QilinCheckin',
  components: { QilinStatusStrip, headerSection: () => import('./Header.vue'), footerSection: () => import('./Footer.vue') },
  data () { return { status: null, loading: true, claiming: false, notice: '', error: '', weekLabels: ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'] } },
  computed: {
    standalonePreview () { return qilinPreviewEnabled && this.$route.query.preview === 'standalone' },
    backRoute () { return this.standalonePreview ? { path: '/pet', query: { preview: 'standalone' } } : '/' },
    depositPercent () { return Math.min(100, Math.round(this.status.todayDeposit / (this.status.minDeposit || 1) * 100)) },
    ladderIndex () { return (Math.max(1, this.status.claimedToday ? this.status.streak : this.status.nextStreak) - 1) % 7 },
    monthLabel () { return moment(this.status.today).locale('th').format('MMMM YYYY') },
    calendarCells () {
      const month = moment(this.status.today).startOf('month')
      const cells = Array.from({ length: month.day() }, () => ({ day: '' }))
      for (let day = 1; day <= month.daysInMonth(); day += 1) {
        const date = month.clone().date(day).format('YYYY-MM-DD')
        cells.push({ day, fed: Boolean(this.status.monthDays[date]), today: date === this.status.today })
      }
      return cells
    }
  },
  mounted () { this.load() },
  methods: {
    money (value) { return Number(value || 0).toLocaleString() },
    async load () {
      this.loading = true
      try { this.status = await qilinApi.getCheckinStatus() } catch (error) { this.error = qilinErrorMessage(error) } finally { this.loading = false }
    },
    async claim () {
      this.claiming = true
      this.notice = ''
      try {
        const response = await qilinApi.claimCheckin()
        const extras = [response.comebackBonus ? `โบนัสกลับมา ${response.comebackBonus} เหรียญ` : '', response.levelCoinBonus ? `โบนัสเลเวล ${response.levelCoinBonus} เหรียญ` : ''].filter(Boolean).join(' · ')
        this.notice = `ให้อาหารสำเร็จ รับ ${response.reward} เหรียญ + ${response.xpGained} XP${extras ? ` · ${extras}` : ''}`
        this.status = await qilinApi.getCheckinStatus()
      } catch (error) { this.notice = qilinErrorMessage(error) } finally { this.claiming = false }
    }
  }
}
</script>

<style scoped>
.checkin-page { min-height: 100vh; background: radial-gradient(circle at 50% 12%, #10245a, #03051b 42%); color: #fff; font-family: 'Kanit', sans-serif; }
.checkin-page__main { width: min(1040px, calc(100% - 28px)); min-height: 76vh; margin: auto; padding: 105px 0 48px; }
.checkin-page__top { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.checkin-page__top > a { display: grid; width: 40px; height: 40px; place-items: center; border: 1px solid rgba(255,255,255,.1); border-radius: 13px; color: #fff; text-decoration: none; }
.checkin-page__top small { color: #68dec0; font-size: 11px; font-weight: 900; }
.checkin-page__top h1 { margin: 0; font-size: 25px; }
.checkin-page__state { min-height: 430px; display: grid; align-content: center; justify-items: center; gap: 10px; color: rgba(255,255,255,.65); }
.checkin-page__state a { color: #ffd85d; }
.checkin-notice { margin-bottom: 13px; padding: 11px 14px; border: 1px solid rgba(103,219,189,.22); border-radius: 13px; background: rgba(103,219,189,.09); color: #baf8e7; font-size: 13px; }
.checkin-grid { display: grid; grid-template-columns: .9fr 1.1fr; gap: 16px; }
.feed-card, .ladder-card, .calendar-card { border: 1px solid rgba(255,255,255,.09); border-radius: 23px; background: rgba(8,15,48,.86); }
.feed-card { display: flex; flex-direction: column; padding: 24px; }
.feed-card__eyebrow { color: #6ce1c1; font-size: 11px; font-weight: 900; letter-spacing: .08em; }
.feed-card h2 { margin: 6px 0; font-size: 27px; }
.feed-card > p { margin: 0 0 20px; color: rgba(255,255,255,.58); font-size: 13px; }
.deposit-meter { padding: 15px; border-radius: 16px; background: rgba(255,255,255,.055); }
.deposit-meter > div:first-child { display: flex; justify-content: space-between; gap: 12px; font-size: 12px; }
.deposit-meter strong { color: #ffe17a; }
.deposit-meter__track { height: 9px; overflow: hidden; margin-top: 11px; border-radius: 99px; background: rgba(0,0,0,.35); }
.deposit-meter__track i { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg,#49d7b3,#fbc844); }
.feed-card > button { min-height: 50px; margin: 15px 0; border: 0; border-radius: 14px; background: linear-gradient(135deg,#ffd85d,#f39a32); color: #181327; font-family: inherit; font-weight: 900; }
.feed-card > button:disabled { opacity: .48; }
aside { display: grid; gap: 16px; }
.ladder-card, .calendar-card { padding: 19px; }
.ladder-card > span { font-size: 14px; font-weight: 900; }
.ladder { display: grid; grid-template-columns: repeat(7,1fr); gap: 6px; margin-top: 13px; }
.ladder > div { padding: 9px 3px; border: 1px solid rgba(255,255,255,.07); border-radius: 12px; background: rgba(255,255,255,.045); text-align: center; }
.ladder > div.active { border-color: #ffd85d; background: rgba(251,200,68,.12); }
.ladder small, .ladder b, .ladder em { display: block; }
.ladder small { color: rgba(255,255,255,.46); font-size: 8px; }
.ladder b { margin: 3px 0 0; color: #ffe17a; font-size: 17px; }
.ladder em { color: rgba(255,255,255,.42); font-size: 8px; font-style: normal; }
.calendar-card__head { display: flex; justify-content: space-between; }
.calendar-card__head span { font-weight: 900; }
.calendar-card__head small { color: rgba(255,255,255,.45); }
.calendar-week, .calendar-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 6px; }
.calendar-week { margin: 14px 0 7px; }
.calendar-week span { color: rgba(255,255,255,.35); font-size: 9px; text-align: center; }
.calendar-grid i { display: grid; aspect-ratio: 1; place-items: center; border-radius: 9px; background: rgba(255,255,255,.04); color: rgba(255,255,255,.52); font-size: 10px; font-style: normal; }
.calendar-grid i.fed { position: relative; background: rgba(89,218,183,.18); color: transparent; }
.calendar-grid i.fed::after { position: absolute; content: '✓'; color: #8cebd1; }
.calendar-grid i.today { outline: 1px solid #ffd85d; }
.calendar-grid i.empty { background: transparent; }
@media (max-width: 820px) { .checkin-grid { grid-template-columns: 1fr; } }
@media (max-width: 575px) { .checkin-page__main { padding-top: 24px; } .feed-card { padding: 18px; } .ladder-card, .calendar-card { padding: 14px; } .ladder { gap: 3px; } }
</style>
